/**
 * Converts an array of objects into CSV format.  The first row will be the headers, and the subsequent rows will be the values.
 *
 * @param rows Array of objects to convert to CSV
 * @returns CSV string
 */
export function toCsv<T extends Record<string, any>>(rows: T[]): string {
  if (!rows.length) return "";

  const headers = Object.keys(rows[0]);
  const escape = (val: any) => `"${String(val ?? "").replace(/"/g, '""')}"`;

  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ].join("\n");

  return csv;
}
/**
 * Extracts only the specified fields from an array of objects.
 * @param rows Array of objects to extract fields from
 * @param fields Array of field names to extract
 * @returns new array of objects with only the specified fields.  This is useful for generating CSVs with only the fields you want.
 *
 */
export function pickFields<T extends Record<string, any>>(
  rows: T[],
  fields: (keyof T)[],
) {
  return rows.map((row) => {
    const obj: Record<string, any> = {};
    fields.forEach((f) => (obj[f as string] = row[f]));
    return obj;
  });
}
/**
 * Extracts summary data from the DOM for CSV export.
 * If you use this, then it's expected you have this defined as summary header.  Data is extracted
 * from the DOM, so it must be present in the print page.
 *
 * Below is sample data/header... but importantly, the data-labels must match the field names in the * summary row object.
 *
 * Note the <div class="print-summary"> and data-label.
 *
 * <div class="print-summary">
 *   <div data-label="Date">19/06/2026</div>
 *   <div data-label="Supplier">ANSTED</div>
 *   <div data-label="NZ Rate">0.50</div>
 *   <div data-label="Comm (%)">0.00</div>
 *   <div data-label="Sell #1">100.00</div>
 *   <div data-label="Sell #2">20.00</div>
 *   <div data-label="Freight">$4,681.96</div>
 * </div>
 *
 * @returns summary row for csv export
 */
export function extractSummaryFromDom() {
  const summaryNodes = document.querySelectorAll(".print-summary [data-label]");

  const rows = Array.from(summaryNodes).map((node) => ({
    Field: node.getAttribute("data-label") ?? "",
    Value: node.textContent?.trim() ?? "",
  }));

  return rows;
}
